import { GATEWAY_BASE_URL } from '../config/gateway';
import type {
  InstitutionWorkspaceDescriptor,
  WorkspaceDescriptor,
  WorkspaceDiscovery,
} from '../types/workspace.types';
import { tokenService } from './tokenService';

const WORKSPACES_ENDPOINT = `${GATEWAY_BASE_URL}/api/v1/me/workspaces`;

type WorkspaceResponseEntry = Record<string, unknown>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const invalidResponse = (message: string): Error =>
  new Error(`Invalid workspace discovery response: ${message}`);

const normalizeWorkspace = (
  workspace: WorkspaceResponseEntry,
  index: number,
): WorkspaceDescriptor => {
  if (workspace.kind === 'platform') {
    if (workspace.institutionId !== undefined || workspace.institutionName !== undefined) {
      throw invalidResponse(`platform workspace ${index} must not include institution details`);
    }

    return {
      kind: 'platform',
      key: 'platform',
      label: 'Platform',
    };
  }

  if (workspace.kind !== 'institution') {
    throw invalidResponse(`workspace ${index} has an unsupported kind`);
  }

  if (!isNonEmptyString(workspace.institutionId)) {
    throw invalidResponse(`institution workspace ${index} is missing institutionId`);
  }

  const institutionName = isNonEmptyString(workspace.institutionName)
    ? workspace.institutionName.trim()
    : undefined;

  const institution: InstitutionWorkspaceDescriptor = {
    kind: 'institution',
    institutionId: workspace.institutionId,
    institutionName,
    key: `institution:${workspace.institutionId}`,
    label: institutionName ?? workspace.institutionId,
  };

  return institution;
};

const normalizeWorkspaceDiscovery = (payload: unknown): WorkspaceDiscovery => {
  if (!isRecord(payload)) {
    throw invalidResponse('body must be an object');
  }

  if (!isNonEmptyString(payload.userId)) {
    throw invalidResponse('userId is required');
  }

  if (!Array.isArray(payload.workspaces)) {
    throw invalidResponse('workspaces must be an array');
  }

  const workspaces = payload.workspaces.map((workspace, index) => {
    if (!isRecord(workspace)) {
      throw invalidResponse(`workspace ${index} must be an object`);
    }

    return normalizeWorkspace(workspace, index);
  });

  const keys = new Set<string>();
  for (const workspace of workspaces) {
    if (keys.has(workspace.key)) {
      throw invalidResponse(`duplicate workspace key "${workspace.key}"`);
    }
    keys.add(workspace.key);
  }

  return {
    userId: payload.userId,
    workspaces,
  };
};

const getErrorMessage = async (response: Response): Promise<string> => {
  const payload = await response.json().catch(() => undefined);

  if (isRecord(payload)) {
    if (isNonEmptyString(payload.message)) return payload.message;
    if (isNonEmptyString(payload.error)) return payload.error;
  }

  return `Workspace discovery request failed (HTTP ${response.status})`;
};

export const workspaceService = {
  async getWorkspaces(): Promise<WorkspaceDiscovery> {
    const token = await tokenService.getAccessToken();
    if (!token) {
      throw new Error('Unable to discover workspaces without an authenticated session');
    }

    // Build headers from the freshly acquired token so this global endpoint never
    // receives the current institution selector.
    const response = await fetch(WORKSPACES_ENDPOINT, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(await getErrorMessage(response));
    }

    const payload = await response.json().catch(() => {
      throw invalidResponse('body must be valid JSON');
    });

    return normalizeWorkspaceDiscovery(payload);
  },
};
